import * as Api from '@ViewModels';
import { IRegEdIntegrationStatus, isRegEdAdminSession } from './types';
import { isRegEdRouted } from './useAccountUsesRegEd';
import { useRegEdIntegrationQuery } from './useRegEdIntegrationQuery';

const isRegEdOfficerFiling = (integration?: IRegEdIntegrationStatus | null): boolean =>
	isRegEdRouted(integration) && integration?.officerFilesSubmissions === true;

/**
 * Whether the firm's compliance officer files through the review portal rather than the advisor filing
 * in-app. Built on [[isRegEdRouted]] so both answers move together — an account that isn't
 * routing through RegEd at all has no officer-filing mode to be in.
 *
 * Fail-closed to advisor filing while loading or on error: guessing the other way would hide the
 * questionnaire that is the account's only way to file.
 */
export const useAccountOfficerFilesRegEd = (
	impersonationContext?: Api.IImpersonationContext,
	{ enabled = true }: { enabled?: boolean } = {}
): boolean => {
	const { data } = useRegEdIntegrationQuery({ impersonationContext, enabled });
	return isRegEdOfficerFiling(data);
};

/**
 * Whether this session must leave the compliance questions to the account's user: an admin from outside the
 * account, unless the officer files. Matches the API, which refuses only those filings.
 *
 * Held to the account's user while officer filing is unknown, so an admin isn't offered the questions before
 * it loads. If the integration can't be read, the admin keeps them: the API still refuses a filing it must.
 */
export const useRegEdAnswersLeftToAccountUser = (
	impersonationContext?: Api.IImpersonationContext
): { leftToAccountUser: boolean; isLoading: boolean } => {
	const adminSession = isRegEdAdminSession(impersonationContext);
	// isLoadingError, not isError: a failed background refetch keeps its data and must not swap flows mid-session.
	const { data, isLoadingError, isLoading } = useRegEdIntegrationQuery({ impersonationContext, enabled: adminSession });
	return {
		isLoading: adminSession && isLoading,
		leftToAccountUser: adminSession && !isLoadingError && !isRegEdOfficerFiling(data),
	};
};
